import styles from './Tab.module.scss';
import Box from '@components/Box';
import cn from 'classnames';
import ColumnList from '../components/СolumnList.tsx';
import DataTable, { DataViewHandle } from '@containers/DataTable';
import DropshippingModalWindow from '../modals/DropshippingModalWindow/DropshippingModalWindow';
import InfoModalWindow from '../modals/InfoModalWindow/InfoModalWindow';
import { AddSellerPanel } from '@components/AddSeller';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import {
  getInProcess,
  TInProcessParcel,
  TEditMatchingExternalParcel
} from '@api/apiClient/dropshipping';
import { IInProcessTabProps } from '../types';
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { TableColumn, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize } from '@components/Text';
import { useSellersCounter } from '@contexts/SellersCounterContext';
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const InProcessTab = (props: IInProcessTabProps) => {
  const {
    handlerOnUpdate,
    isFilterOpen,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenDropshippingWindow, setIsOpenDropshippingWindow] = useState<boolean>(false);
  const [isOpenInfoWindow, setIsOpenInfoWindow] = useState<boolean>(false);
  const [externalMatchingId, setExternalMatchingId] = useState<string>("");
  const [linkedItems, setLinkedItems] = useState<string[]>([]);
  const [linkedItemsComment, setLinkedItemsComment] = useState<string>('');
  const [linkedItemsMatchingId, setLinkedItemsMatchingId] = useState<string>('');
  const [linkedItemsTrackNumbers, setLinkedItemsTrackNumbers] = useState<TEditMatchingExternalParcel[]>([]);

  const dataRef = useRef<DataViewHandle>(null);
  const { sellersNotFound } = useSellersCounter();
  const { t } = useTranslation();

  const refreshData = () => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    handlerOnUpdate();
  };

  const columns: TableColumn<TInProcessParcel>[] = [
    {
      header: <div className={styles.columnList}>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.trackNumber')}</span>
        <span className={styles.columnListText}/>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.name')}</span>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.orderNumberOzon')}</span>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.otherMarketplaceNumber')}</span>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.commentToWarehouse')}</span>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.seller')}</span>
        <span className={styles.columnListText}>{t('dropshipping.inProcessTab.connectionDate')}</span>
      </div>,
      accessor: (row: TableRowType<TInProcessParcel>) => (
          <ColumnList
              calledFrom="inProcess"
              data={row.data}
              handlerSetIsOpenDropshippingWindow={setIsOpenDropshippingWindow}
              handlerSetIsOpenInfoWindow={(externalId: string) => {
                setExternalMatchingId(externalId)
                setIsOpenInfoWindow(true);
              }}
              handlerSetLinkedItems={setLinkedItems}
              handlerSetLinkedItemsComment={setLinkedItemsComment}
              handlerSetLinkedItemsMatchingId={setLinkedItemsMatchingId}
              handlerSetLinkedItemsTrackNumbers={setLinkedItemsTrackNumbers}
          />
      ),
      noOverflow: true
    }
  ];

  if (sellersNotFound) {
    return (
        <AddSellerPanel
            isFirstSeller
            isOzonSeller
        />
    );
  }

  return (
      <>
        <Box pb={5}>
          <Text
              color={TextColor.PRIMARY}
              size={TextSize.S}
          >
            {t('dropshipping.inProcessTab.description')}
          </Text>
        </Box>
        <Box
            pb={5}
            className={cn(!isFilterOpen && styles.hidden)}
        >
          <Column phone={12}>
            <Row
                justify="right"
                gap={5}
            >
              <Filters
                  id="dropshipping-inProcess"
                  openFilters={() => setIsFilterOpen(true)}
                  items={[
                    {
                      name: 'ExternalIds',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('dropshipping.inProcessTab.orderNumberOzon')
                    },
                    {
                      name: 'ExternalMarketplaceTrackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('dropshipping.inProcessTab.otherMarketplaceNumber')
                    },
                    {
                      name: 'MatchingTrackNumbers',
                      type: FilterTypes.MULTI_SEARCH,
                      placeholder: t('dropshipping.inProcessTab.matchingTrackNumbers')
                    },
                    {
                      name: 'sellerIds',
                      type: FilterTypes.SELLER
                    },
                    {
                      name: 'dateRange',
                      type: FilterTypes.DATE_RANGE,
                      placeholder: t('dropshipping.inProcessTab.connectionDate'),
                      fieldNames: ['startDate', 'endDate']
                    }
                  ]}
                  onChange={(v, isReady) => {
                    if (isReady) {
                      setIsInitialized(true);
                    }
                    setInnerFilters(v);
                  }}
                  values={innerFilters}
                  asPanel
              />
            </Row>
          </Column>
        </Box>
        <DataTable<TInProcessParcel>
            columns={columns}
            dataRef={dataRef}
            defaultPageSize={10}
            filters={innerFilters}
            getData={getInProcess}
            notFound={t('dropshipping.noParcels')}
            pending={!isInitialized}
            queryKey="dropshippingInProcessParcels"
        />

        {isOpenDropshippingWindow ? (
          <DropshippingModalWindow
            calledFrom="inProcess"
            handlerRefreshData={refreshData}
            onClose={() => setIsOpenDropshippingWindow(false)}
            linkedItems={linkedItems}
            linkedItemsComment={linkedItemsComment}
            linkedItemsMatchingId={linkedItemsMatchingId}
            linkedItemsTrackNumbers={linkedItemsTrackNumbers}
          />
        ) : null}

        <Modal
            isOpen={isOpenInfoWindow}
            onClickOverlay={() => setIsOpenInfoWindow(false)}
            onClose={() => setIsOpenInfoWindow(false)}
            width={650}
        >
          <InfoModalWindow
              handlerRefreshData={refreshData}
              handlerSetIsOpenInfoWindow={setIsOpenInfoWindow}
              matchingId={linkedItemsMatchingId}
              isPaidMatching={Boolean(externalMatchingId)}
          />
        </Modal>
      </>
  );
};

export default InProcessTab;
